/**
 * The portfolio's Needs you tab: a to-do list, in the style of the loan
 * tape's cards.
 *
 * Two groups: what the portfolio itself shows (a plat that doesn't cover its
 * debt, rent not collected, houses losing money), then problems in the data
 * that was sent. Each card offers what can be done about it: answer its
 * question, upload what is missing, open the plat, or mark it as known. An
 * answer is saved beside the tables, so the Analyst reads it too, and a done
 * item moves to the bottom where it can be reopened.
 */

import { Check, Upload } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import type { ReviewItem, ReviewState } from "../../services/portfolioDataService";

const KIND_LABEL: Record<string, string> = {
	risk: "Portfolio finding",
	conflict: "Conflicting values",
	unusable: "Can't be used as printed",
	empty: "Blank in your export",
	missing: "Not sent yet",
};

const BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:opacity-50";
const PRIMARY = `${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`;
const SECONDARY = `${BUTTON} border-[#99CFCD] bg-white text-[#004C4B] hover:bg-[#f6fbfb]`;
const QUIET = `${BUTTON} border-gray-200 bg-white text-[#3f4247] hover:bg-gray-50`;

export interface PortfolioNeedsYouProps {
	items: readonly ReviewItem[];
	state: ReviewState;
	/** The item being saved, so its buttons wait. */
	saving: string | null;
	onOpenPlat: (platId: string) => void;
	/** Every house, for a finding that spans plats. */
	onOpenHouses: (houseIds: string[]) => void;
	onUpdate: (itemId: string, status: "open" | "known" | "answered", answer?: string) => void;
	/** Where the Deal Room's upload is, for an item asking for a file. */
	uploadHref: string | null;
}

const Card: React.FC<{
	item: ReviewItem;
	done: ReviewState[string] | undefined;
	saving: boolean;
	props: PortfolioNeedsYouProps;
}> = ({ item, done, saving, props }) => {
	const portfolio = item.area === "portfolio";
	return (
		<section
			aria-label={item.title}
			className={`rounded-xl border bg-white ${done ? "border-gray-200 opacity-75" : portfolio ? "border-[#fedf89]" : "border-gray-200"}`}
		>
			<div className="flex flex-wrap items-start gap-3 px-[18px] py-3.5">
				<div className="min-w-0 flex-1">
					<p className="text-[15px] font-semibold text-[#111111]">{item.title}</p>
					<p className="mt-1 text-[13px] leading-relaxed text-[#3f4247]">{item.detail}</p>
					{item.cell && <p className="mt-1 font-mono text-xs text-[#6B7280]">{item.cell}</p>}
				</div>
				{done ? (
					<div className="flex flex-shrink-0 items-center gap-2">
						<span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-[#ecfdf3] px-2 text-[11.5px] font-semibold text-[#067647]">
							<Check className="h-3 w-3" aria-hidden />
							{done.status === "answered" ? `Answered: ${done.answer}` : "Marked as known"}
						</span>
						<button
							type="button"
							disabled={saving}
							onClick={() => props.onUpdate(item.id, "open")}
							className="text-[13px] font-medium text-[#004C4B] underline underline-offset-[3px]"
						>
							Reopen
						</button>
					</div>
				) : (
					<div className="flex flex-shrink-0 flex-wrap items-center gap-2">
						{item.plat_id ? (
							<button type="button" onClick={() => props.onOpenPlat(item.plat_id as string)} className={portfolio ? PRIMARY : SECONDARY}>
								See the houses
							</button>
						) : item.houses && item.houses.length > 0 ? (
							<button type="button" onClick={() => props.onOpenHouses(item.houses ?? [])} className={PRIMARY}>
								See the houses
							</button>
						) : null}
						{item.upload &&
							(props.uploadHref ? (
								<Link to={props.uploadHref} className={PRIMARY}>
									<Upload className="h-4 w-4" aria-hidden />
									{item.upload}
								</Link>
							) : (
								<span className="text-xs text-[#6B7280]">Add it to the deal's files to rebuild these tables.</span>
							))}
						<button type="button" disabled={saving} onClick={() => props.onUpdate(item.id, "known")} className={QUIET}>
							Mark as known
						</button>
					</div>
				)}
			</div>
			{!done && item.choices && item.choices.length > 0 && (
				<div className="flex flex-wrap items-center gap-2 border-t border-[#f1f2f4] px-[18px] py-2.5">
					<span className="mr-1 text-[13px] font-semibold text-[#1F2937]">{item.question ?? "Which is right?"}</span>
					{item.choices.map((choice) => (
						<button
							key={choice}
							type="button"
							disabled={saving}
							onClick={() => props.onUpdate(item.id, "answered", choice)}
							className={SECONDARY}
						>
							{choice}
						</button>
					))}
					<span className="text-xs text-[#6B7280]">The Analyst uses your answer from the next question on.</span>
				</div>
			)}
			<div className="flex items-center gap-2.5 border-t border-[#f1f2f4] px-[18px] py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#b54708]">
				{KIND_LABEL[item.kind] ?? "Check"}
			</div>
		</section>
	);
};

const PortfolioNeedsYou: React.FC<PortfolioNeedsYouProps> = (props) => {
	const { items, state, saving } = props;
	const open = items.filter((item) => !state[item.id]);
	const done = items.filter((item) => state[item.id]);
	const group = (label: string, list: readonly ReviewItem[]) =>
		list.length === 0 ? null : (
			<section className="flex flex-col gap-3">
				<h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
					{label} · {list.length}
				</h2>
				{list.map((item) => (
					<Card key={item.id} item={item} done={state[item.id]} saving={saving === item.id} props={props} />
				))}
			</section>
		);
	if (items.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
				Nothing needs you: the tables match your workbook and nothing in them conflicts.
			</p>
		);
	}
	return (
		<div className="flex flex-col gap-6">
			{open.length === 0 && (
				<p className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm text-[#5B6472]">
					Everything here has been answered or marked as known.
				</p>
			)}
			{group("In the portfolio", open.filter((item) => item.area === "portfolio"))}
			{group("In the data you sent", open.filter((item) => item.area !== "portfolio"))}
			{group("Done", done)}
		</div>
	);
};

export default PortfolioNeedsYou;
